<template>
  <div
    class="drop-zone"
    :class="{ 'is-dragging': isDragging }"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="fileInput"
      type="file"
      accept=".csv,.xlsx,.xls"
      class="hidden-input"
      @change="onFileSelect"
    />

    <p v-if="!file">
      {{ $t("app.dragzone.dropFilesHere") }}
      <span class="browse" @click="openFileDialog">{{ $t("app.dragzone.browseFiles") }}</span>
    </p>

    <div v-else>
      <p>{{ file.name }} ({{ formatSize(file.size) }})<span class="remove-file-btn" @click="removeFile">x</span></p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { useToast } from 'vue-toastification';

const toast = useToast();

/* =========================
  Emits
========================= */
const emit = defineEmits<{
  (e: 'file-selected', file: File): void
  (e: 'file-removed', removedFile: File): void
}>();

/* =========================
  Reactive State
========================= */
const isDragging = ref<boolean>(false);
const file = ref<File>(undefined);
const fileInput = ref<HTMLInputElement | null>(null);

/* =========================
  Event Handlers
========================= */
function onDragOver(): void {
  isDragging.value = true;
}

function onDragLeave(): void {
  isDragging.value = false;
}

function isValidFileType(file: File): boolean {
  const validTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
  const validExtensions = ['.csv', '.xlsx', '.xls'];
  
  const hasValidType = validTypes.includes(file.type);
  const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

  if (!hasValidType && !hasValidExtension) {
    toast.error('Unsupported file format');
  }
  return hasValidType || hasValidExtension;
}

function onDrop(event: DragEvent): void {
  isDragging.value = false;

  if (!event.dataTransfer) return;
  if (!!file.value) {
    removeFile();
  }

  const droppedFiles: File[] = Array.from(event.dataTransfer.files);
  const validFiles = droppedFiles.filter(isValidFileType);
  file.value = validFiles[0];

  emit('file-selected', file.value);
}

function onFileSelect(event: Event): void {
  const target = event.target as HTMLInputElement;
  if (!target.files) return;

  const selectedFiles: File[] = Array.from(target.files).filter(isValidFileType);
  file.value = selectedFiles[0];

  emit('file-selected', file.value);
}

function removeFile(): void {
  fileInput.value.value = "";
  emit('file-removed', file.value);
  file.value = null;
}

function openFileDialog(): void {
  fileInput.value?.click();
}

/* =========================
   Utilities
========================= */
function formatSize(size: number): string {
  return (size / 1024).toFixed(1) + ' KB';
}
</script>

<style scoped lang="scss">
@import "@/assets/style/variables.scss";

.drop-zone {
  border: 2px dashed #aaa;
  padding: 40px;
  text-align: center;
  border-radius: 8px;
  transition: 0.2s ease;
  cursor: pointer;
}

.drop-zone.is-dragging {
  border-color: $primary-color;
  background-color: var(--white-color);
}

.hidden-input {
  display: none;
}

.browse {
  color: $primary-color;
  cursor: pointer;
  text-decoration: underline;
}

.remove-file-btn {
  color: red;
  cursor: pointer;
  margin-left: 10px;
  font-size: 18px;
}
</style>
